import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiUpload, FiUsers } from 'react-icons/fi';
import { rosterAPI, instituteAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import RosterStudentModal from './RosterStudentModal';

const Roster = () => {
  const { isSuperAdmin, access } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const instituteUuid = searchParams.get('instituteUuid') || '';

  const [institutes, setInstitutes] = useState([]);
  const [students, setStudents] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [importSummary, setImportSummary] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // superAdmin isn't tied to a district — load the institute list so they can pick one
  useEffect(() => {
    if (isSuperAdmin() && institutes.length === 0) {
      instituteAPI.getAll().then((res) => setInstitutes(res.data || [])).catch(() => {});
    }
  }, [isSuperAdmin, institutes.length]);

  const scopeParams = instituteUuid ? { instituteUuid } : {};
  const canLoad = !isSuperAdmin() || !!instituteUuid;

  const fetchStudents = useCallback(async () => {
    if (!canLoad) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await rosterAPI.getAll({ ...scopeParams, page, limit: 25, search: search || undefined });
      setStudents(response.data || []);
      setPagination(response.pagination || null);
      setError('');
    } catch (err) {
      setError('Failed to load roster');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instituteUuid, page, search, canLoad]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleAdd = () => {
    setSelectedStudent(null);
    setShowModal(true);
  };

  const handleEdit = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm(`Remove student ${studentId} from the roster?`)) return;
    try {
      await rosterAPI.delete(studentId, scopeParams);
      fetchStudents();
    } catch (err) {
      alert('Failed to remove student');
    }
  };

  const handleSave = async (formData) => {
    if (selectedStudent) {
      await rosterAPI.update(selectedStudent['Student ID'], formData, scopeParams);
    } else {
      await rosterAPI.add(formData, scopeParams);
    }
    setShowModal(false);
    fetchStudents();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportSummary(null);
    try {
      const response = await rosterAPI.bulkImport(file, scopeParams);
      setImportSummary(response.data);
      fetchStudents();
    } catch (err) {
      setError(err.message || 'Failed to import CSV');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (isSuperAdmin() && !instituteUuid) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Students</h1>
          <p className="text-gray-600 mb-6">Choose a district to manage its student roster.</p>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
            {institutes.map((inst) => (
              <button
                key={inst.uuid}
                onClick={() => setSearchParams({ instituteUuid: inst.uuid })}
                className="w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <span className="font-medium text-gray-900">{inst.name}</span>
                <span className="text-sm text-gray-400">{inst.shortcode}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Manage Students</h1>
            <p className="text-gray-600">
              {access?.instituteName ? `Roster for ${access.instituteName}` : 'Add and manage the student roster'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <label className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
              <FiUpload className="h-5 w-5 mr-2" />
              {importing ? 'Importing...' : 'Upload CSV'}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelected}
                disabled={importing}
                className="hidden"
              />
            </label>
            <button
              onClick={handleAdd}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <FiPlus className="h-5 w-5 mr-2" />
              Add Student
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
        )}

        {importSummary && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
            Imported {importSummary.totalRows} rows — {importSummary.upserted} added, {importSummary.modified} updated
            {importSummary.skippedNoId ? `, ${importSummary.skippedNoId} skipped (no Student ID)` : ''}.
          </div>
        )}

        <div className="mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by name or Student ID..."
            className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
          {loading ? (
            <div className="p-12 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : students.length === 0 ? (
            <div className="p-12 text-center">
              <FiUsers className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No students in the roster yet</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grade</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campus</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {students.map((s) => (
                  <tr key={s['Student ID']} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 font-mono">{s['Student ID']}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{s['First Name']} {s['Last Name']}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{s['Grade Level'] ?? '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{s['Campus/Entity'] || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{s['Email Address'] || '—'}</td>
                    <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                      <button onClick={() => handleEdit(s)} className="text-blue-600 hover:text-blue-800">
                        <FiEdit2 className="h-4 w-4 inline" />
                      </button>
                      <button onClick={() => handleDelete(s['Student ID'])} className="text-red-600 hover:text-red-800">
                        <FiTrash2 className="h-4 w-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">Page {pagination.currentPage} of {pagination.totalPages}</span>
            <button
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <RosterStudentModal
          student={selectedStudent}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default Roster;
